import { useState, useEffect, useRef, useCallback } from 'react';

declare global {
  interface Window { Plivo: any; }
}

export type PlivoDeviceState = 'offline' | 'registering' | 'registered' | 'error';
export type PlivoCallState = 'idle' | 'calling' | 'ringing' | 'connected';

export interface UsePlivoDeviceReturn {
  plivoState: PlivoDeviceState;
  plivoCallState: PlivoCallState;
  plivoError: string | null;
  makePlivoCall: (phoneNumber: string) => void;
  hangupPlivoCall: () => void;
  answerPlivoCall: () => void;
  sendPlivoDTMF: (digit: string) => void;
  togglePlivoMute: () => void;
  isMuted: boolean;
  isPlivoReady: boolean;
  incomingPlivoCall: any | null;
}

let plivoInstance: any = null;
let sdkLoaded = false;
let ringbackCtx: AudioContext | null = null;
let ringbackNodes: { osc: OscillatorNode; gain: GainNode }[] = [];
let ringbackTimer: ReturnType<typeof setTimeout> | null = null;

function stopRingback() {
  if (ringbackTimer) { clearTimeout(ringbackTimer); ringbackTimer = null; }
  ringbackNodes.forEach(({ osc, gain }) => {
    try { gain.gain.setValueAtTime(0, ringbackCtx!.currentTime); osc.stop(); } catch {}
  });
  ringbackNodes = [];
  if (ringbackCtx) {
    try { ringbackCtx.close(); } catch {}
    ringbackCtx = null;
  }
}

function startRingback() {
  stopRingback();
  try {
    ringbackCtx = new AudioContext();
    const ctx = ringbackCtx;

    const playTone = (freq: number, startTime: number, duration: number) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = freq;
      osc.type = 'sine';
      gain.gain.setValueAtTime(1.0, startTime);
      gain.gain.setValueAtTime(0.9, startTime + duration - 0.02);
      gain.gain.linearRampToValueAtTime(0, startTime + duration);
      osc.start(startTime);
      osc.stop(startTime + duration);
      ringbackNodes.push({ osc, gain });
    };

    const now = ctx.currentTime;
    // 2 beeps
    playTone(440, now, 0.15);
    playTone(440, now + 0.25, 0.15);
    // Ringback: 1s on, 2s off x 3
    for (let i = 0; i < 3; i++) {
      const t = now + 0.6 + i * 3.0;
      playTone(400, t, 1.0);
      playTone(450, t, 1.0);
    }
  } catch (e) {
    console.warn('[Plivo] Ringback audio error:', e);
  }
}

let incomingRingTimer: ReturnType<typeof setInterval> | null = null;

function startIncomingRing() {
  stopIncomingRing();
  const playRing = () => {
    try {
      const ctx = new AudioContext();
      const compressor = ctx.createDynamicsCompressor();
      compressor.connect(ctx.destination);
      const playTone = (freq: number, start: number, dur: number) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(compressor);
        osc.frequency.value = freq;
        osc.type = 'triangle';
        gain.gain.setValueAtTime(1.5, start);
        gain.gain.setValueAtTime(1.5, start + dur - 0.05);
        gain.gain.linearRampToValueAtTime(0, start + dur);
        osc.start(start);
        osc.stop(start + dur);
      };
      const now = ctx.currentTime;
      playTone(480, now, 0.4);
      playTone(420, now + 0.05, 0.4);
      playTone(480, now + 0.6, 0.4);
      playTone(420, now + 0.65, 0.4);
      setTimeout(() => ctx.close().catch(() => {}), 2500);
    } catch(e) { console.warn('Ring error:', e); }
  };
  playRing();
  incomingRingTimer = setInterval(playRing, 3000);
}

function stopIncomingRing() {
  if (incomingRingTimer) {
    clearInterval(incomingRingTimer);
    incomingRingTimer = null;
  }
}

function getOrCreateRemoteAudio(): HTMLAudioElement {
  let el = document.getElementById('plivo-remote-audio') as HTMLAudioElement;
  if (!el) {
    el = document.createElement('audio');
    el.id = 'plivo-remote-audio';
    el.autoplay = true;
    el.style.display = 'none';
    document.body.appendChild(el);
  }
  return el;
}

function stopRemoteAudio() {
  const remoteAudio = document.getElementById('plivo-remote-audio') as HTMLAudioElement;
  if (remoteAudio) {
    remoteAudio.srcObject = null;
    remoteAudio.pause();
  }
}

function loadPlivoSdk(): Promise<void> {
  if (sdkLoaded || window.Plivo) { sdkLoaded = true; return Promise.resolve(); }
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://cdn.plivo.com/sdk/browser/v2/plivo.min.js';
    script.onload = () => { sdkLoaded = true; resolve(); };
    script.onerror = () => reject(new Error('Failed to load Plivo SDK'));
    document.head.appendChild(script);
  });
}

export function usePlivoDevice(enabled: boolean = false): UsePlivoDeviceReturn {
  const [plivoState, setPlivoState] = useState<PlivoDeviceState>('offline');
  const [plivoCallState, setPlivoCallState] = useState<PlivoCallState>('idle');
  const [plivoError, setPlivoError] = useState<string | null>(null);
  const [isMuted, setIsMuted] = useState(false);
  const [incomingPlivoCall, setIncomingPlivoCall] = useState<any | null>(null);
  const initDoneRef = useRef(false);
  const isDialingRef = useRef(false);

  const resetCallState = useCallback(() => {
    console.log('[Plivo] Resetting call state');
    stopRingback();
    stopRemoteAudio();
    setPlivoCallState('idle');
    setIncomingPlivoCall(null);
    setIsMuted(false);
    isDialingRef.current = false;
  }, []);

  const initPlivo = useCallback(async () => {
    if (initDoneRef.current || !enabled) return;
    initDoneRef.current = true;

    try {
      setPlivoState('registering');
      await loadPlivoSdk();

      const res = await window.axios.get('/plivo/token');
      const { username, password } = res.data;

      const options = {
        debug: 'OFF',
        permOnClick: false,
        codecs: ['OPUS', 'PCMU'],
        enableIPV6: false,
        audioConstraints: { optional: [{ googAutoGainControl: false }] },
        dscp: true,
        enableTracking: false,
      };

      plivoInstance = new window.Plivo(options);
      const client = plivoInstance.client;

      client.on('onLogin', () => {
        console.log('[Plivo] Registered ✅');
        setPlivoState('registered');
        setPlivoError(null);
      });

      client.on('onLoginFailed', (cause: string) => {
        console.error('[Plivo] Login failed:', cause);
        setPlivoState('error');
        setPlivoError('Plivo registration failed: ' + cause);
        initDoneRef.current = false;
      });

      client.on('onLogout', () => {
        setPlivoState('offline');
        resetCallState();
      });

      // Outbound: remote side is ringing
      client.on('onCallRemoteRinging', () => {
        console.log('[Plivo] Remote ringing');
        setPlivoCallState('ringing');
        startRingback();
      });

      // Call answered — stop ringback, attach audio
      client.on('onCallAnswered', () => {
        console.log('[Plivo] Call answered ✅');
        stopRingback();
        stopIncomingRing();
        setPlivoCallState('connected');
        // Attach remote audio stream
        try {
          const remoteAudio = getOrCreateRemoteAudio();
          // Try multiple ways to get remote stream
          const stream = client.getRemoteStream?.()
            || client.remoteStream
            || client.activeCall?.getRemoteStream?.()
            || null;
          if (stream) {
            console.log('[Plivo] Attaching remote stream to audio element');
            remoteAudio.srcObject = stream;
            remoteAudio.play().catch((e: any) => console.warn('[Plivo] Audio play error:', e));
          } else {
            console.warn('[Plivo] No remote stream available yet, waiting for onMediaConnected');
          }
        } catch (e) {
          console.warn('[Plivo] Audio attachment error:', e);
        }
      });

      client.on('onMediaConnected', () => {
        console.log('[Plivo] Media connected ✅');
        stopRingback();
        // Don't set connected here — wait for onCallAnswered
        // Just attach audio stream if available
        try {
          const remoteAudio = getOrCreateRemoteAudio();
          const stream = client.getRemoteStream?.()
            || client.remoteStream
            || null;
          if (stream && !remoteAudio.srcObject) {
            remoteAudio.srcObject = stream;
            remoteAudio.play().catch((e: any) => console.warn('[Plivo] Audio play error:', e));
          }
        } catch (e) {
          console.warn('[Plivo] Media connected audio error:', e);
        }
      });

      // ── Termination events — ALL of these reset state ──
      const handleTermination = (reason: string) => (info?: any) => {
        console.log(`[Plivo] Call ended (${reason})`, info);
        stopIncomingRing();
        resetCallState();
      };

      client.on('onCallTerminated', handleTermination('terminated'));
      client.on('onCallHangup', handleTermination('hangup'));
      client.on('onMediaDisconnected', handleTermination('media-disconnected'));
      client.on('onCallFailed', (cause: string) => {
        console.error('[Plivo] Call failed:', cause);
        setPlivoError('Call failed: ' + cause);
        resetCallState();
      });

      // Incoming call
      client.on('onIncomingCall', (callInfo: any, headers: any) => {
        // Extract caller number from all possible fields
        // callInfo can be a string like "+919891065150@phone.plivo.com" or an object
        let callerFrom = 'Unknown';
        if (typeof callInfo === 'string') {
          // Extract number from SIP URI string
          callerFrom = callInfo.replace(/@.*$/, '').replace(/^sip:/, '').replace(/^\+/, '+') || 'Unknown';
        } else if (callInfo && typeof callInfo === 'object') {
          callerFrom = callInfo?.from || callInfo?.number || callInfo?.caller_id
            || callInfo?.remote_identity || callInfo?.From || 'Unknown';
        }
        // Ensure it starts with + for display
        if (callerFrom !== 'Unknown' && !callerFrom.startsWith('+')) {
          callerFrom = '+' + callerFrom;
        }
        console.log('[Plivo] Incoming call - full callInfo:', JSON.stringify(callInfo));
        console.log('[Plivo] Incoming call - headers:', JSON.stringify(headers));
        console.log('[Plivo] Incoming call - resolved from:', callerFrom);
        setIncomingPlivoCall({ callInfo, headers, from: callerFrom });
        setPlivoCallState('ringing');
        startIncomingRing();
      });

      client.on('onIncomingCallCanceled', () => {
        console.log('[Plivo] Incoming call canceled');
        stopIncomingRing();
        resetCallState();
      });

      // Request mic permission
      try {
        await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
        console.log('[Plivo] Microphone permission granted ✅');
      } catch (e) {
        console.warn('[Plivo] Mic permission denied:', e);
        setPlivoError('Microphone permission denied. Please allow mic access.');
      }

      client.login(username, password);

    } catch (err: any) {
      console.error('[Plivo] Init error:', err);
      setPlivoState('error');
      setPlivoError(err.message);
      initDoneRef.current = false;
    }
  }, [enabled, resetCallState]);

  useEffect(() => {
    if (enabled) { initPlivo(); }
    return () => {
      if (plivoInstance && !enabled) {
        try { plivoInstance.client.logout(); } catch {}
        plivoInstance = null;
        initDoneRef.current = false;
        setPlivoState('offline');
        resetCallState();
      }
    };
  }, [enabled, initPlivo, resetCallState]);

  useEffect(() => {
    if (plivoCallState === 'idle') {
      isDialingRef.current = false;
      stopRingback();
    }
  }, [plivoCallState]);

  const makePlivoCall = useCallback((phoneNumber: string) => {
    if (isDialingRef.current) {
      console.warn('[Plivo] Duplicate call blocked');
      return;
    }
    if (!plivoInstance || plivoState !== 'registered') {
      console.warn('[Plivo] Not ready:', plivoState, '— retrying in 2s');
      // Retry once after 2 seconds
      setTimeout(() => {
        if (plivoInstance && plivoState === 'registered') {
          makePlivoCall(phoneNumber);
        } else {
          console.error('[Plivo] Still not ready after retry');
        }
      }, 2000);
      return;
    }
    isDialingRef.current = true;
    const clean = phoneNumber.replace(/\s/g, '').replace(/^\+/, '');
    console.log('[Plivo] Calling:', clean);
    setPlivoCallState('calling');
    try {
      plivoInstance.client.call(clean);
    } catch (e: any) {
      console.error('[Plivo] call() error:', e);
      setPlivoError('Failed to start call: ' + e.message);
      resetCallState();
    }
  }, [plivoState, resetCallState]);

  const hangupPlivoCall = useCallback(() => {
    console.log('[Plivo] Hanging up');
    stopRingback();
    if (plivoInstance) {
      try { plivoInstance.client.hangup(); } catch (e) {
        console.error('[Plivo] hangup error:', e);
      }
    }
    resetCallState();
  }, [resetCallState]);

  const answerPlivoCall = useCallback(() => {
    if (!plivoInstance || !incomingPlivoCall) return;
    try {
      stopRingback();
      plivoInstance.client.answer();
    } catch (e) {
      console.error('[Plivo] answer error:', e);
    }
  }, [incomingPlivoCall]);

  const sendPlivoDTMF = useCallback((digit: string) => {
    if (plivoInstance && plivoCallState === 'connected') {
      try { plivoInstance.client.sendDtmf(digit); } catch {}
    }
  }, [plivoCallState]);

  const togglePlivoMute = useCallback(() => {
    if (!plivoInstance) return;
    try {
      if (isMuted) { plivoInstance.client.unmute(); }
      else { plivoInstance.client.mute(); }
      setIsMuted(prev => !prev);
    } catch (e) {
      console.error('[Plivo] mute error:', e);
    }
  }, [isMuted]);

  return {
    plivoState,
    plivoCallState,
    plivoError,
    makePlivoCall,
    hangupPlivoCall,
    answerPlivoCall,
    sendPlivoDTMF,
    togglePlivoMute,
    isMuted,
    isPlivoReady: plivoState === 'registered',
    incomingPlivoCall,
  };
}
